const DEFAULT_URL = 'ws://127.0.0.1:9876';
const CONNECT_TIMEOUT_MS = 1500;
const DEBUG_SERVER_URL = 'http://127.0.0.1:7777/event';
const DEBUG_SESSION_ID = 'print-websocket-cursor';

function reportPrintAgentDebug(hypothesisId, msg, data = {}) {
  fetch(DEBUG_SERVER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      sessionId: DEBUG_SESSION_ID,
      runId: 'pre-fix',
      hypothesisId,
      location: 'frontend/src/lib/printAgent.js',
      msg: `[DEBUG] ${msg}`,
      data,
      ts: Date.now(),
    }),
  }).catch(() => {});
}

export function createPrintAgent(url = DEFAULT_URL, { autoReconnect = false } = {}) {
  let socket = null;
  let online = false;
  let stopped = false;
  let reconnectTimer = null;
  let reconnectDelay = 1000;
  let connectPromise = null;
  const pending = new Map();
  const listeners = new Set();

  function notify() {
    for (const fn of listeners) fn(online);
  }

  function setOnline(nextOnline) {
    if (online === nextOnline) return;
    // #region debug-point A:online-state-change
    reportPrintAgentDebug('A', 'print-agent online state changed', { previousOnline: online, nextOnline });
    // #endregion
    online = nextOnline;
    notify();
  }

  function failPending(message) {
    for (const { reject } of pending.values()) reject(new Error(message));
    pending.clear();
  }

  function clearReconnectTimer() {
    if (!reconnectTimer) return;
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  function scheduleReconnect() {
    if (!autoReconnect || stopped || reconnectTimer) return;
    // #region debug-point A:schedule-reconnect
    reportPrintAgentDebug('A', 'scheduled websocket reconnect', { reconnectDelay, stopped, autoReconnect });
    // #endregion
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, 10000);
  }

  function connect() {
    stopped = false;
    clearReconnectTimer();

    if (socket?.readyState === WebSocket.OPEN) return Promise.resolve(true);
    if (socket?.readyState === WebSocket.CONNECTING) {
      return connectPromise || Promise.resolve(false);
    }
    if (connectPromise) return connectPromise;

    connectPromise = new Promise((resolve) => {
      let settled = false;
      let timeoutId = null;

      const finish = (result) => {
        if (settled) return;
        settled = true;
        connectPromise = null;
        if (timeoutId) clearTimeout(timeoutId);
        resolve(result);
      };

      // #region debug-point A:connect-start
      reportPrintAgentDebug('A', 'starting websocket connection', {
        url,
        hasSocket: Boolean(socket),
        readyState: socket?.readyState ?? null,
        autoReconnect,
      });
      // #endregion
      const nextSocket = new WebSocket(url);
      socket = nextSocket;

      nextSocket.addEventListener('open', () => {
        // #region debug-point A:socket-open
        reportPrintAgentDebug('A', 'websocket open event', { url, readyState: nextSocket.readyState });
        // #endregion
        reconnectDelay = 1000;
        setOnline(true);
        finish(true);
      });
      nextSocket.addEventListener('close', () => {
        // #region debug-point A:socket-close
        reportPrintAgentDebug('A', 'websocket close event', { url, readyState: nextSocket.readyState });
        // #endregion
        if (socket === nextSocket) socket = null;
        setOnline(false);
        failPending('Print Agent desconectado');
        finish(false);
        scheduleReconnect();
      });
      nextSocket.addEventListener('error', () => {
        // #region debug-point A:socket-error
        reportPrintAgentDebug('A', 'websocket error event', { url, readyState: nextSocket.readyState });
        // #endregion
        setOnline(false);
        finish(false);
        scheduleReconnect();
      });
      nextSocket.addEventListener('message', (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          // #region debug-point A:message-parse-failed
          reportPrintAgentDebug('A', 'websocket message parse failed', { rawType: typeof event.data });
          // #endregion
          return;
        }
        if (message.type === 'job_status') {
          // #region debug-point A:job-status
          reportPrintAgentDebug('A', 'received websocket job status', {
            jobId: message.jobId ?? null,
            status: message.status ?? null,
          });
          // #endregion
          if (message.status === 'SENDING') return;
          const entry = pending.get(message.jobId);
          if (!entry) return;
          pending.delete(message.jobId);
          if (message.status === 'COMPLETED') entry.resolve(message);
          else entry.reject(new Error(message.error || `Falha: ${message.status}`));
        }
      });

      timeoutId = setTimeout(() => {
        // #region debug-point A:connect-timeout
        reportPrintAgentDebug('A', 'websocket connection timeout', { timeoutMs: CONNECT_TIMEOUT_MS, url });
        // #endregion
        if (socket === nextSocket && nextSocket.readyState === WebSocket.CONNECTING) {
          nextSocket.close();
          socket = null;
        }
        finish(false);
        scheduleReconnect();
      }, CONNECT_TIMEOUT_MS);
    });

    return connectPromise;
  }

  function disconnect() {
    // #region debug-point A:disconnect
    reportPrintAgentDebug('A', 'disconnect requested', { hasSocket: Boolean(socket), readyState: socket?.readyState ?? null });
    // #endregion
    stopped = true;
    clearReconnectTimer();
    if (socket) socket.close();
    socket = null;
    connectPromise = null;
    setOnline(false);
  }

  function sendJob(job) {
    return new Promise((resolve, reject) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Print Agent não encontrado'));
        return;
      }
      pending.set(job.jobId, { resolve, reject });
      socket.send(JSON.stringify({ type: 'print_job', ...job }));
    });
  }

  function deleteJob(jobId) {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'delete_job', jobId }));
    }
  }

  function clearJobs() {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'clear_jobs' }));
    }
  }

  function listPorts() {
    return new Promise((resolve, reject) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Print Agent não encontrado'));
        return;
      }
      const onMessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.type === 'ports') {
          socket.removeEventListener('message', onMessage);
          resolve(message.ports);
        }
      };
      socket.addEventListener('message', onMessage);
      socket.send(JSON.stringify({ type: 'list_ports' }));
    });
  }

  function listPrinters() {
    return new Promise((resolve, reject) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Print Agent não encontrado'));
        return;
      }
      const onMessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.type === 'printers') {
          socket.removeEventListener('message', onMessage);
          resolve(message);
        }
      };
      socket.addEventListener('message', onMessage);
      socket.send(JSON.stringify({ type: 'list_printers' }));
    });
  }

  function listInstalled() {
    return new Promise((resolve, reject) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Print Agent não encontrado'));
        return;
      }
      const onMessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message.type === 'installed') {
          socket.removeEventListener('message', onMessage);
          resolve(message);
        }
      };
      socket.addEventListener('message', onMessage);
      socket.send(JSON.stringify({ type: 'list_installed' }));
    });
  }

  function isOnline() {
    return online;
  }

  function onChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  }

  return { connect, disconnect, sendJob, deleteJob, clearJobs, listPorts, listPrinters, listInstalled, isOnline, onChange };
}
